import { dropdownMenuContainer } from '@/utils/portalContainer'
import { Popover, type PopoverProps } from '@mantine/core'
import { isEmpty } from 'lodash-es'
import {
	CSSProperties,
	type HTMLProps,
	type MouseEvent,
	type ReactNode,
	type Ref,
	useImperativeHandle,
	useState,
} from 'react'
import DropdownMenuItems from './DropdownMenuItems'
import type { MenuItemData } from './types'

export interface DropdownMenuProps {
	renderTrigger: (props: HTMLProps<HTMLElement>) => ReactNode
	data: MenuItemData[]
	onSelect: (id: MenuItemData['id'], data: MenuItemData, e: MouseEvent<HTMLElement>) => void
	dropdownStyles?: CSSProperties
	position?: PopoverProps['position']
	ref?: Ref<DropdownMenuHandle>
}

export interface DropdownMenuHandle {
	isOpen: boolean
	openPortal: () => void
	closePortal: () => void
	moveToNext: () => void
	moveToPrevious: () => void
	activeItem: MenuItemData | null
}

function DropdownMenu({
	renderTrigger,
	data,
	onSelect,
	dropdownStyles,
	position = 'top-start',
	ref,
}: DropdownMenuProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [activeId, setActiveId] = useState<string | null>(null)

	useImperativeHandle(
		ref,
		() => ({
			isOpen,
			openPortal: () => setIsOpen(true),
			closePortal: () => setIsOpen(false),
			moveToNext: () => {
				if (!activeId) {
					setActiveId(data[0]?.id)
					return
				}
				const currentIndex = data.findIndex((item) => item.id === activeId)
				if (currentIndex < data.length - 1) {
					setActiveId(data[currentIndex + 1].id)
				}
			},
			moveToPrevious: () => {
				if (!activeId) {
					setActiveId(data[data.length - 1]?.id)
					return
				}
				const currentIndex = data.findIndex((item) => item.id === activeId)
				if (currentIndex > 0) {
					setActiveId(data[currentIndex - 1].id)
				}
			},
			activeItem: data.find((item) => item.id === activeId) || null,
		}),
		[activeId, data, isOpen]
	)

	if (isEmpty(data)) {
		return renderTrigger({})
	}

	return (
		<Popover
			opened={isOpen}
			onChange={setIsOpen}
			position={position}
			offset={4}
			width={160}
			floatingStrategy="absolute"
			unstyled
			styles={{
				dropdown: {
					width: '160px',
					padding: '4px',
					borderRadius: '10px',
					background: 'var(--bg-on-body-1, #fff)',
					fontSize: '14px',
					lineHeight: '20px',
					boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.24)',
					position: 'absolute',
					...dropdownStyles,
				},
			}}
			portalProps={{
				target: dropdownMenuContainer,
			}}
		>
			<Popover.Target>
				{renderTrigger({
					onClick: () => setIsOpen(!isOpen),
					'aria-expanded': isOpen,
					'aria-haspopup': true,
					role: 'button',
					tabIndex: 0,
				})}
			</Popover.Target>

			<Popover.Dropdown>
				<DropdownMenuItems
					data={data}
					activeId={activeId}
					onSelect={onSelect}
					onClose={() => setIsOpen(false)}
				/>
			</Popover.Dropdown>
		</Popover>
	)
}

export default DropdownMenu
